/**
 * Direct Messages Service
 * API calls for DM thread operations
 */

import apiClient from './api.client';
import type { ApiResponse, DMThread } from '@/types/api.types';

/**
 * Create or get existing DM thread
 * Accepts single user ID (1:1) or array of user IDs (group)
 */
export const createDMThread = async (userIds: string | string[]): Promise<DMThread> => {
  const ids = Array.isArray(userIds) ? userIds : [userIds];
  const response = await apiClient.post<ApiResponse<{ thread: DMThread }>>(
    '/dm-threads',
    { userIds: ids }
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to create DM thread');
  }

  return response.data.data.thread;
};

/**
 * List all DM threads for current user
 */
export const listDMThreads = async (): Promise<DMThread[]> => {
  const response = await apiClient.get<ApiResponse<{ threads: DMThread[] }>>(
    '/dm-threads'
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch DM threads');
  }

  return response.data.data.threads;
};

/**
 * Get a single DM thread by ID
 */
export const getDMThread = async (threadId: string): Promise<DMThread> => {
  const response = await apiClient.get<ApiResponse<{ thread: DMThread }>>(
    `/dm-threads/${threadId}`
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch DM thread');
  }

  return response.data.data.thread;
};

/**
 * Delete a DM thread
 */
export const deleteDMThread = async (threadId: string): Promise<void> => {
  const response = await apiClient.delete<ApiResponse>(
    `/dm-threads/${threadId}`
  );

  if (response.data.status === 'error') {
    throw new Error(response.data.error || 'Failed to delete DM thread');
  }
};

/**
 * Get list of all users (for user picker)
 */
export const listUsers = async (): Promise<Array<{
  id: string;
  display_name: string;
  email: string;
  avatar_url: string | null;
}>> => {
  const response = await apiClient.get<ApiResponse<{ users: Array<{
    id: string;
    display_name: string;
    email: string;
    avatar_url: string | null;
  }> }>>(
    '/users'
  );

  if (!response.data.data) {
    throw new Error(response.data.error || 'Failed to fetch users');
  }

  return response.data.data.users;
};

/**
 * Mark DM thread as read (updates last_read_at)
 */
export const markDMThreadAsRead = async (threadId: string): Promise<void> => {
  const response = await apiClient.post<ApiResponse>(
    `/dm-threads/${threadId}/read`
  );

  if (response.data.status !== 'success') {
    throw new Error(response.data.error || 'Failed to mark DM thread as read');
  }
};
